import type { Agent } from '@atproto/api'
import { MACKINAC_XRPC_URL, MACKINAC_SERVICE_DID } from '@/constants/api'
import type { FeatureFlags } from '@/types/featureFlags'

const GET_FLAGS_NSID = 'social.mackinac.features.getFlags'
const SET_FLAG_NSID = 'social.mackinac.features.setFlag'

export interface LabsFlagInfo {
    flagName: string
    displayName: string | null
    description: string | null
}

export interface FeatureFlagsResponse {
    flags: FeatureFlags
    isAdmin: boolean
    labsFlags: LabsFlagInfo[]
}

export async function fetchFeatureFlags(agent: Agent): Promise<FeatureFlagsResponse> {
    try {
        const { data } = await agent.com.atproto.server.getServiceAuth({
            aud: MACKINAC_SERVICE_DID,
            lxm: GET_FLAGS_NSID,
        })

        const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${GET_FLAGS_NSID}`, {
            headers: { Authorization: `Bearer ${data.token}` },
        })

        if (!response.ok) {
            return { flags: {} as FeatureFlags, isAdmin: false, labsFlags: [] }
        }

        const body = await response.json()
        return {
            flags: body.flags as FeatureFlags,
            isAdmin: body.isAdmin === true,
            labsFlags: Array.isArray(body.labsFlags) ? (body.labsFlags as LabsFlagInfo[]) : [],
        }
    } catch {
        return { flags: {} as FeatureFlags, isAdmin: false, labsFlags: [] }
    }
}

export async function setFeatureFlag(
    agent: Agent,
    flagName: string,
    enabled: boolean
): Promise<void> {
    try {
        const { data } = await agent.com.atproto.server.getServiceAuth({
            aud: MACKINAC_SERVICE_DID,
            lxm: SET_FLAG_NSID,
        })

        const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${SET_FLAG_NSID}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${data.token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ flagName, enabled }),
        })

        if (!response.ok) {
            throw new Error(`setFeatureFlag failed: ${response.status}`)
        }
    } catch (err) {
        console.error('[setFeatureFlag]', err)
        throw new Error('Failed to update feature flag. Please try again.')
    }
}
